import React, { useState, useEffect, useCallback, useRef } from "react";
import Layout from "../layout/Layout";
import LoadingOverlay from "react-loading-overlay-ts";
import Api from "../Config/ApiConfig";
import Pagination from "../Component/Pagination";
import { toast } from "react-toastify";
import moment from "moment";

// Method Badge Styling
const METHOD_STYLES = {
  GET: "bg-emerald-100 text-emerald-800 border-emerald-300",
  POST: "bg-blue-100 text-blue-800 border-blue-300",
  PUT: "bg-amber-100 text-amber-800 border-amber-300",
  PATCH: "bg-purple-100 text-purple-800 border-purple-300",
  DELETE: "bg-rose-100 text-rose-800 border-rose-300",
  OPTIONS: "bg-gray-100 text-gray-800 border-gray-300"
};

// Target Application Configuration
const TARGET_APP_CONFIG = {
  b4t_react_admin: {
    label: "Admin Panel",
    badge: "bg-purple-100 text-purple-800 border-purple-300",
    icon: "fas fa-shield-alt"
  },
  b4t_website_nuxt: {
    label: "Live Website",
    badge: "bg-sky-100 text-sky-800 border-sky-300",
    icon: "fas fa-globe"
  },
  b4t_matchboard: {
    label: "Matchboard",
    badge: "bg-emerald-100 text-emerald-800 border-emerald-300",
    icon: "fas fa-route"
  },
  b4t_agent: {
    label: "Partner / Agent",
    badge: "bg-amber-100 text-amber-800 border-amber-300",
    icon: "fas fa-user-tie"
  },
  page_editor: {
    label: "Page Editor",
    badge: "bg-indigo-100 text-indigo-800 border-indigo-300",
    icon: "fas fa-edit"
  },
  mobile_app: {
    label: "Mobile App",
    badge: "bg-teal-100 text-teal-800 border-teal-300",
    icon: "fas fa-mobile-alt"
  },
  other: {
    label: "Direct / Other",
    badge: "bg-gray-100 text-gray-700 border-gray-300",
    icon: "fas fa-terminal"
  }
};

export default function ApiLogs() {
  const [loading, setLoading] = useState(false);
  const [logs, setLogs] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, pages: 1 });
  const [stats, setStats] = useState({
    total: 0,
    errors: 0,
    warnings: 0,
    success: 0,
    success_rate: 100,
    avg_latency_ms: 0
  });

  // Filter States
  const [levelFilter, setLevelFilter] = useState("all");
  const [methodFilter, setMethodFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [targetFilter, setTargetFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [datePreset, setDatePreset] = useState("all_time");
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");
  const [autoRefresh, setAutoRefresh] = useState(false);

  // cURL Auth Token Preference
  const [includeAuthInQuickCopy, setIncludeAuthInQuickCopy] = useState(true);
  const [includeAuthInModalCurl, setIncludeAuthInModalCurl] = useState(true);

  // Inspector Modal State
  const [selectedLog, setSelectedLog] = useState(null);
  const [copiedCurlId, setCopiedCurlId] = useState(null);
  const [copiedSection, setCopiedSection] = useState(null);

  // Deletion / Storage Modal State
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletionPreset, setDeletionPreset] = useState("older_than_30_days");
  const [deletionStartDate, setDeletionStartDate] = useState("");
  const [deletionEndDate, setDeletionEndDate] = useState("");
  const [deletionCount, setDeletionCount] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isCountingDeletion, setIsCountingDeletion] = useState(false);

  const autoRefreshTimerRef = useRef(null);

  // Compute date boundaries based on datePreset
  const getDateRangeParams = useCallback(() => {
    const now = moment();
    if (datePreset === "today") {
      return {
        startDate: now.startOf("day").toISOString(),
        endDate: now.endOf("day").toISOString()
      };
    }
    if (datePreset === "last_7_days") {
      return {
        startDate: now.clone().subtract(7, "days").toISOString(),
        endDate: now.toISOString()
      };
    }
    if (datePreset === "last_30_days") {
      return {
        startDate: now.clone().subtract(30, "days").toISOString(),
        endDate: now.toISOString()
      };
    }
    if (datePreset === "custom") {
      return {
        startDate: customStartDate ? new Date(customStartDate).toISOString() : undefined,
        endDate: customEndDate ? new Date(customEndDate).toISOString() : undefined
      };
    }
    return {};
  }, [datePreset, customStartDate, customEndDate]);

  // Fetch Logs from Backend
  const fetchLogs = useCallback(
    async (pageToFetch = pagination.page, showSpinner = true) => {
      try {
        if (showSpinner) setLoading(true);
        const dateParams = getDateRangeParams();

        const params = new URLSearchParams({
          page: pageToFetch,
          limit: pagination.limit
        });

        if (levelFilter !== "all") params.append("level", levelFilter);
        if (methodFilter !== "all") params.append("method", methodFilter);
        if (statusFilter !== "all") params.append("status_code", statusFilter);
        if (targetFilter !== "all") params.append("target_app", targetFilter);
        if (searchTerm.trim()) params.append("search", searchTerm.trim());
        if (dateParams.startDate) params.append("startDate", dateParams.startDate);
        if (dateParams.endDate) params.append("endDate", dateParams.endDate);

        const res = await Api.get(`api-logs?${params.toString()}`);
        if (res.data?.success) {
          setLogs(res.data.data.logs || []);
          setPagination(res.data.data.pagination || { page: 1, limit: 20, total: 0, pages: 1 });
          if (res.data.data.stats) {
            setStats(res.data.data.stats);
          }
        }
      } catch (error) {
        console.error("Failed to load API logs:", error);
        toast.error(error.response?.data?.message || "Failed to load API logs");
      } finally {
        if (showSpinner) setLoading(false);
      }
    },
    [
      pagination.page,
      pagination.limit,
      levelFilter,
      methodFilter,
      statusFilter,
      targetFilter,
      searchTerm,
      getDateRangeParams
    ]
  );

  // Initial fetch and dependency trigger
  useEffect(() => {
    fetchLogs(1);
  }, [fetchLogs, levelFilter, methodFilter, statusFilter, targetFilter, datePreset, customStartDate, customEndDate]);

  // Auto-refresh interval handler
  useEffect(() => {
    if (autoRefresh) {
      autoRefreshTimerRef.current = setInterval(() => {
        fetchLogs(pagination.page, false);
      }, 10000);
    } else {
      if (autoRefreshTimerRef.current) clearInterval(autoRefreshTimerRef.current);
    }
    return () => {
      if (autoRefreshTimerRef.current) clearInterval(autoRefreshTimerRef.current);
    };
  }, [autoRefresh, fetchLogs, pagination.page]);

  // Page Change Handler
  const handlePageChange = ({ selected }) => {
    const nextPage = selected + 1;
    fetchLogs(nextPage);
  };

  // Inspect Single Log
  const handleInspectLog = async (log) => {
    try {
      setLoading(true);
      const res = await Api.get(`api-logs/${log._id}`);
      if (res.data?.success) {
        setSelectedLog(res.data.data);
      } else {
        setSelectedLog(log);
      }
    } catch (err) {
      setSelectedLog(log);
    } finally {
      setLoading(false);
    }
  };

  // Copy cURL with visual feedback and token preference
  const handleCopyCurl = (log, id, forceWithToken = null) => {
    const shouldUseToken =
      forceWithToken !== null ? forceWithToken : includeAuthInQuickCopy;
    const curlText =
      shouldUseToken && log.curl_with_token ? log.curl_with_token : log.curl;

    if (!curlText) return;
    navigator.clipboard.writeText(curlText);
    setCopiedCurlId(id);

    if (shouldUseToken && log.curl_with_token && log.auth_token) {
      toast.success("cURL command with Auth Token copied!");
    } else {
      toast.success("cURL command copied to clipboard!");
    }
    setTimeout(() => setCopiedCurlId(null), 2500);
  };

  // Copy JSON snippet
  const handleCopySnippet = (content, key) => {
    const str = typeof content === "object" ? JSON.stringify(content, null, 2) : String(content);
    navigator.clipboard.writeText(str);
    setCopiedSection(key);
    toast.success("Copied to clipboard!");
    setTimeout(() => setCopiedSection(null), 2000);
  };

  // Fetch Deletion Preview Count
  const fetchDeletionCount = useCallback(async () => {
    try {
      setIsCountingDeletion(true);
      const params = new URLSearchParams({ preset: deletionPreset });
      if (deletionPreset === "custom_range") {
        if (deletionStartDate) params.append("startDate", new Date(deletionStartDate).toISOString());
        if (deletionEndDate) params.append("endDate", new Date(deletionEndDate).toISOString());
      }
      const res = await Api.get(`api-logs/count-deletion?${params.toString()}`);
      if (res.data?.success) {
        setDeletionCount(res.data.data.count);
      }
    } catch (err) {
      console.error("Error calculating deletion count:", err);
      setDeletionCount(null);
    } finally {
      setIsCountingDeletion(false);
    }
  }, [deletionPreset, deletionStartDate, deletionEndDate]);

  useEffect(() => {
    if (showDeleteModal) {
      fetchDeletionCount();
    }
  }, [showDeleteModal, deletionPreset, deletionStartDate, deletionEndDate, fetchDeletionCount]);

  // Execute Log Deletion
  const handleDeleteLogs = async () => {
    try {
      setIsDeleting(true);
      const payload = {
        preset: deletionPreset,
        startDate: deletionStartDate ? new Date(deletionStartDate).toISOString() : undefined,
        endDate: deletionEndDate ? new Date(deletionEndDate).toISOString() : undefined
      };

      const res = await Api.delete("api-logs", { data: payload });
      if (res.data?.success) {
        toast.success(res.data.message || `Deleted ${res.data.data.deleted_count} logs successfully!`);
        setShowDeleteModal(false);
        fetchLogs(1);
      }
    } catch (error) {
      console.error("Failed to delete logs:", error);
      toast.error(error.response?.data?.message || "Failed to delete logs");
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Layout>
      <LoadingOverlay active={loading} spinner text="Loading API telemetry logs...">
        <div className="p-4 md:p-6 space-y-6 max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 bg-white p-5 rounded-2xl shadow-sm border border-gray-100">
            <div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center text-lg font-bold">
                  <i className="fas fa-terminal"></i>
                </div>
                <div>
                  <h1 className="text-xl font-bold text-gray-900">API Request & Response Logs</h1>
                  <p className="text-xs text-gray-500">
                    Live system telemetry, target caller classification, executable cURL generator, and retention management
                  </p>
                </div>
              </div>
            </div>

            {/* Quick Actions & Preferences */}
            <div className="flex flex-wrap items-center gap-2.5">
              {/* Quick cURL Auth Token Toggle */}
              <label
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-gray-50 border border-gray-200 text-gray-700 cursor-pointer hover:bg-gray-100 transition-colors shadow-2xs"
                title="When enabled, clicking cURL copies command with authentic Bearer/Auth Token included"
              >
                <input
                  type="checkbox"
                  checked={includeAuthInQuickCopy}
                  onChange={(e) => setIncludeAuthInQuickCopy(e.target.checked)}
                  className="rounded text-indigo-600 focus:ring-indigo-500 w-3.5 h-3.5"
                />
                <i className="fas fa-key text-amber-500 text-[11px]"></i>
                <span>Copy cURL with Auth Token</span>
              </label>

              {/* Auto Refresh Toggle */}
              <button
                type="button"
                onClick={() => setAutoRefresh(!autoRefresh)}
                className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                  autoRefresh
                    ? "bg-emerald-50 text-emerald-700 border-emerald-300 hover:bg-emerald-100"
                    : "bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100"
                }`}
                title="Auto-fetch latest logs every 10 seconds"
              >
                <span
                  className={`w-2 h-2 rounded-full ${autoRefresh ? "bg-emerald-500 animate-ping" : "bg-gray-400"}`}
                ></span>
                <span>Auto-Refresh: {autoRefresh ? "ON" : "OFF"}</span>
              </button>

              {/* Refresh Button */}
              <button
                type="button"
                onClick={() => fetchLogs(pagination.page)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 transition-colors"
              >
                <i className="fas fa-sync-alt"></i>
                <span>Refresh</span>
              </button>

              {/* Delete / Storage Management Button */}
              <button
                type="button"
                onClick={() => setShowDeleteModal(true)}
                className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-xs font-semibold bg-rose-50 text-rose-700 border border-rose-200 hover:bg-rose-100 transition-colors shadow-xs"
              >
                <i className="fas fa-trash-alt text-rose-500"></i>
                <span>Manage Storage & Prune</span>
              </button>
            </div>
          </div>

          {/* Stats Bar */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3.5">
            {/* Total */}
            <div className="bg-white p-4 rounded-xl shadow-xs border border-gray-100 flex flex-col justify-between">
              <span className="text-xs font-medium text-gray-500">Total Tracked</span>
              <div className="flex items-baseline justify-between mt-2">
                <span className="text-2xl font-bold text-gray-900">{stats.total.toLocaleString()}</span>
                <span className="text-xs text-gray-400">All calls</span>
              </div>
            </div>

            {/* Errors (5xx) */}
            <div
              onClick={() => setLevelFilter(levelFilter === "error" ? "all" : "error")}
              className={`cursor-pointer p-4 rounded-xl shadow-xs border transition-all flex flex-col justify-between ${
                levelFilter === "error"
                  ? "bg-rose-100 border-rose-400 ring-2 ring-rose-300"
                  : "bg-rose-50/70 border-rose-200 hover:bg-rose-100/50"
              }`}
            >
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-rose-700">Server Errors (5xx)</span>
                <i className="fas fa-exclamation-circle text-rose-500 text-sm"></i>
              </div>
              <div className="flex items-baseline justify-between mt-2">
                <span className="text-2xl font-bold text-rose-800">{stats.errors.toLocaleString()}</span>
                <span className="text-[11px] text-rose-600 font-medium">Click to filter</span>
              </div>
            </div>

            {/* Warnings (4xx) */}
            <div
              onClick={() => setLevelFilter(levelFilter === "warning" ? "all" : "warning")}
              className={`cursor-pointer p-4 rounded-xl shadow-xs border transition-all flex flex-col justify-between ${
                levelFilter === "warning"
                  ? "bg-amber-100 border-amber-400 ring-2 ring-amber-300"
                  : "bg-amber-50/70 border-amber-200 hover:bg-amber-100/50"
              }`}
            >
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-amber-700">Client Warnings (4xx)</span>
                <i className="fas fa-exclamation-triangle text-amber-500 text-sm"></i>
              </div>
              <div className="flex items-baseline justify-between mt-2">
                <span className="text-2xl font-bold text-amber-800">{stats.warnings.toLocaleString()}</span>
                <span className="text-[11px] text-amber-600 font-medium">Click to filter</span>
              </div>
            </div>

            {/* Success (2xx) */}
            <div
              onClick={() => setLevelFilter(levelFilter === "info" ? "all" : "info")}
              className={`cursor-pointer p-4 rounded-xl shadow-xs border transition-all flex flex-col justify-between ${
                levelFilter === "info"
                  ? "bg-emerald-100 border-emerald-400 ring-2 ring-emerald-300"
                  : "bg-emerald-50/70 border-emerald-200 hover:bg-emerald-100/50"
              }`}
            >
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-emerald-700">Success Rate</span>
                <i className="fas fa-check-circle text-emerald-500 text-sm"></i>
              </div>
              <div className="flex items-baseline justify-between mt-2">
                <span className="text-2xl font-bold text-emerald-800">{stats.success_rate}%</span>
                <span className="text-[11px] text-emerald-600 font-medium">{stats.success} successful</span>
              </div>
            </div>

            {/* Latency */}
            <div className="bg-white p-4 rounded-xl shadow-xs border border-gray-100 flex flex-col justify-between col-span-2 md:col-span-1">
              <span className="text-xs font-medium text-gray-500">Avg Latency</span>
              <div className="flex items-baseline justify-between mt-2">
                <span className="text-2xl font-bold text-gray-900">{stats.avg_latency_ms} ms</span>
                <span className="text-xs text-gray-400"><i className="fas fa-clock mr-1"></i>Response</span>
              </div>
            </div>
          </div>

          {/* Filter Toolbar */}
          <div className="bg-white p-4 rounded-2xl shadow-sm border border-gray-100 space-y-3.5">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3">
              {/* Target App Filter */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">API Target (Client)</label>
                <select
                  value={targetFilter}
                  onChange={(e) => setTargetFilter(e.target.value)}
                  className="w-full text-xs rounded-lg border-gray-300 focus:ring-indigo-500 focus:border-indigo-500 p-2 border bg-gray-50/50"
                >
                  <option value="all">All Targets</option>
                  <option value="b4t_react_admin">Admin Panel (b4t_react_admin)</option>
                  <option value="b4t_website_nuxt">Live Website (b4t_website_nuxt)</option>
                  <option value="b4t_matchboard">Matchboard (b4t_matchboard)</option>
                  <option value="b4t_agent">Partner / Agent (b4t_agent)</option>
                  <option value="page_editor">Page Editor (page_editor)</option>
                  <option value="mobile_app">Mobile App (Flutter/Mobile)</option>
                  <option value="other">Direct / Other</option>
                </select>
              </div>

              {/* Level Filter */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Log Level</label>
                <select
                  value={levelFilter}
                  onChange={(e) => setLevelFilter(e.target.value)}
                  className="w-full text-xs rounded-lg border-gray-300 focus:ring-indigo-500 focus:border-indigo-500 p-2 border bg-gray-50/50"
                >
                  <option value="all">All Levels (2xx, 4xx, 5xx)</option>
                  <option value="errors_and_warnings">Errors & Warnings Only</option>
                  <option value="error">Errors Only (5xx)</option>
                  <option value="warning">Warnings Only (4xx)</option>
                  <option value="info">Success Only (2xx/3xx)</option>
                </select>
              </div>

              {/* Method Filter */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">HTTP Method</label>
                <select
                  value={methodFilter}
                  onChange={(e) => setMethodFilter(e.target.value)}
                  className="w-full text-xs rounded-lg border-gray-300 focus:ring-indigo-500 focus:border-indigo-500 p-2 border bg-gray-50/50"
                >
                  <option value="all">All Methods</option>
                  <option value="GET">GET</option>
                  <option value="POST">POST</option>
                  <option value="PUT">PUT</option>
                  <option value="PATCH">PATCH</option>
                  <option value="DELETE">DELETE</option>
                  <option value="OPTIONS">OPTIONS</option>
                </select>
              </div>

              {/* Status Code Filter */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Status Code</label>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="w-full text-xs rounded-lg border-gray-300 focus:ring-indigo-500 focus:border-indigo-500 p-2 border bg-gray-50/50"
                >
                  <option value="all">All Status Codes</option>
                  <option value="2xx">2xx (Success)</option>
                  <option value="4xx">4xx (Client Error)</option>
                  <option value="5xx">5xx (Server Error)</option>
                  <option value="200">200 OK</option>
                  <option value="201">201 Created</option>
                  <option value="400">400 Bad Request</option>
                  <option value="401">401 Unauthorized</option>
                  <option value="403">403 Forbidden</option>
                  <option value="404">404 Not Found</option>
                  <option value="500">500 Server Error</option>
                </select>
              </div>

              {/* Date Preset Filter */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Date Range</label>
                <select
                  value={datePreset}
                  onChange={(e) => setDatePreset(e.target.value)}
                  className="w-full text-xs rounded-lg border-gray-300 focus:ring-indigo-500 focus:border-indigo-500 p-2 border bg-gray-50/50"
                >
                  <option value="all_time">All Time</option>
                  <option value="today">Today</option>
                  <option value="last_7_days">Last 7 Days</option>
                  <option value="last_30_days">Last 30 Days</option>
                  <option value="custom">Custom Date Range...</option>
                </select>
              </div>

              {/* Search Bar */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Search Keywords</label>
                <div className="relative">
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && fetchLogs(1)}
                    placeholder="Search URL, error, target, IP..."
                    className="w-full text-xs rounded-lg border-gray-300 focus:ring-indigo-500 focus:border-indigo-500 pl-8 pr-7 py-2 border bg-gray-50/50"
                  />
                  <i className="fas fa-search absolute left-2.5 top-2.5 text-gray-400 text-xs"></i>
                  {searchTerm && (
                    <button
                      onClick={() => {
                        setSearchTerm("");
                        fetchLogs(1);
                      }}
                      className="absolute right-2 top-2 text-gray-400 hover:text-gray-600 text-xs"
                    >
                      <i className="fas fa-times"></i>
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Custom Date Range Selector (Rendered when custom is chosen) */}
            {datePreset === "custom" && (
              <div className="flex flex-wrap items-center gap-3 p-3 bg-indigo-50/50 rounded-xl border border-indigo-100">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium text-indigo-900">Start Date:</span>
                  <input
                    type="datetime-local"
                    value={customStartDate}
                    onChange={(e) => setCustomStartDate(e.target.value)}
                    className="text-xs rounded-md border-gray-300 p-1.5 border bg-white"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium text-indigo-900">End Date:</span>
                  <input
                    type="datetime-local"
                    value={customEndDate}
                    onChange={(e) => setCustomEndDate(e.target.value)}
                    className="text-xs rounded-md border-gray-300 p-1.5 border bg-white"
                  />
                </div>
                <button
                  type="button"
                  onClick={() => fetchLogs(1)}
                  className="px-3 py-1.5 rounded-md bg-indigo-600 text-white text-xs font-medium hover:bg-indigo-700 transition-colors"
                >
                  Apply Date Filter
                </button>
              </div>
            )}
          </div>

          {/* Logs Table */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse text-xs">
                <thead>
                  <tr className="bg-gray-50/90 text-gray-600 font-semibold border-b border-gray-100 uppercase text-[11px] tracking-wider">
                    <th className="py-3 px-4">Status</th>
                    <th className="py-3 px-3">Method</th>
                    <th className="py-3 px-3">Target Application</th>
                    <th className="py-3 px-4">Path / Endpoint</th>
                    <th className="py-3 px-4">Error Reason</th>
                    <th className="py-3 px-3">Latency</th>
                    <th className="py-3 px-3">Client IP / User</th>
                    <th className="py-3 px-4">Timestamp</th>
                    <th className="py-3 px-4 text-center">Quick Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {logs.length === 0 ? (
                    <tr>
                      <td colSpan="9" className="py-12 text-center text-gray-400">
                        <div className="flex flex-col items-center justify-center">
                          <i className="fas fa-terminal text-3xl mb-2 text-gray-300"></i>
                          <p className="text-sm font-medium">No API logs match your current filter criteria.</p>
                          <button
                            onClick={() => {
                              setLevelFilter("all");
                              setMethodFilter("all");
                              setStatusFilter("all");
                              setTargetFilter("all");
                              setSearchTerm("");
                              setDatePreset("all_time");
                            }}
                            className="mt-2 text-xs text-indigo-600 hover:underline"
                          >
                            Reset all filters
                          </button>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    logs.map((log) => {
                      const isError = log.level === "error" || log.status_code >= 500;
                      const isWarning = log.level === "warning" || (log.status_code >= 400 && log.status_code < 500);
                      const targetConfig = TARGET_APP_CONFIG[log.target_app] || TARGET_APP_CONFIG.other;

                      // Row highlighting
                      let rowStyle = "hover:bg-gray-50/70 border-l-4 border-transparent transition-colors";
                      if (isError) {
                        rowStyle = "bg-rose-50/60 hover:bg-rose-100/60 border-l-4 border-rose-500 transition-colors";
                      } else if (isWarning) {
                        rowStyle = "bg-amber-50/60 hover:bg-amber-100/60 border-l-4 border-amber-500 transition-colors";
                      }

                      return (
                        <tr key={log._id} className={rowStyle}>
                          {/* Status Code & Level */}
                          <td className="py-3 px-4 whitespace-nowrap">
                            <div className="flex items-center gap-1.5">
                              <span
                                className={`inline-flex items-center justify-center font-bold px-2 py-0.5 rounded text-xs border ${
                                  isError
                                    ? "bg-rose-100 text-rose-800 border-rose-300"
                                    : isWarning
                                    ? "bg-amber-100 text-amber-800 border-amber-300"
                                    : "bg-emerald-100 text-emerald-800 border-emerald-300"
                                }`}
                              >
                                {log.status_code}
                              </span>
                              {isError && (
                                <span className="text-[10px] uppercase font-bold text-rose-600 bg-rose-200/60 px-1 rounded">
                                  ERR
                                </span>
                              )}
                              {isWarning && (
                                <span className="text-[10px] uppercase font-bold text-amber-700 bg-amber-200/60 px-1 rounded">
                                  WARN
                                </span>
                              )}
                            </div>
                          </td>

                          {/* HTTP Method */}
                          <td className="py-3 px-3 whitespace-nowrap">
                            <span
                              className={`inline-block px-2 py-0.5 rounded text-[11px] font-bold border ${
                                METHOD_STYLES[log.method] || "bg-gray-100 text-gray-700 border-gray-300"
                              }`}
                            >
                              {log.method}
                            </span>
                          </td>

                          {/* Target Application Badge */}
                          <td className="py-3 px-3 whitespace-nowrap">
                            <span
                              className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-[11px] font-semibold border ${
                                targetConfig.badge
                              }`}
                              title={`Client: ${log.target_app || "other"}`}
                            >
                              <i className={`${targetConfig.icon} text-[10px]`}></i>
                              <span>{log.target_label || targetConfig.label}</span>
                            </span>
                          </td>

                          {/* Path / Endpoint */}
                          <td className="py-3 px-4">
                            <div className="max-w-xs md:max-w-sm truncate font-mono text-gray-800" title={log.url}>
                              {log.path || log.url}
                            </div>
                            {log.query && Object.keys(log.query).length > 0 && (
                              <span className="text-[10px] text-gray-400 font-mono block truncate">
                                ?{new URLSearchParams(log.query).toString()}
                              </span>
                            )}
                          </td>

                          {/* Error Reason */}
                          <td className="py-3 px-4">
                            {log.error_reason ? (
                              <div
                                className={`flex items-center gap-1.5 max-w-xs truncate font-medium ${
                                  isError ? "text-rose-700" : "text-amber-800"
                                }`}
                                title={log.error_reason}
                              >
                                <i
                                  className={`fas ${
                                    isError ? "fa-times-circle text-rose-500" : "fa-exclamation-triangle text-amber-600"
                                  } text-xs shrink-0`}
                                ></i>
                                <span className="truncate">{log.error_reason}</span>
                              </div>
                            ) : (
                              <span className="text-gray-300 italic text-[11px]">None (OK)</span>
                            )}
                          </td>

                          {/* Latency */}
                          <td className="py-3 px-3 whitespace-nowrap font-mono">
                            <span
                              className={`${
                                log.duration_ms > 1000
                                  ? "text-rose-600 font-semibold"
                                  : log.duration_ms > 300
                                  ? "text-amber-700"
                                  : "text-gray-600"
                              }`}
                            >
                              {log.duration_ms} ms
                            </span>
                          </td>

                          {/* Client IP & User */}
                          <td className="py-3 px-3 whitespace-nowrap">
                            <div className="text-gray-700 font-mono text-[11px]">{log.ip || "unknown"}</div>
                            {log.user_email && (
                              <div className="text-[10px] text-indigo-600 truncate max-w-[120px]" title={log.user_email}>
                                {log.user_email}
                              </div>
                            )}
                          </td>

                          {/* Timestamp */}
                          <td className="py-3 px-4 whitespace-nowrap text-gray-500">
                            <div>{moment(log.createdAt).format("DD MMM YYYY")}</div>
                            <div className="text-[10px] text-gray-400">{moment(log.createdAt).format("HH:mm:ss")}</div>
                          </td>

                          {/* Quick Actions */}
                          <td className="py-3 px-4 text-center whitespace-nowrap">
                            <div className="inline-flex items-center gap-1.5">
                              {/* 1-Click Copy cURL Button */}
                              <button
                                type="button"
                                onClick={() => handleCopyCurl(log, log._id)}
                                className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-semibold border transition-all ${
                                  copiedCurlId === log._id
                                    ? "bg-emerald-600 text-white border-emerald-600"
                                    : "bg-white text-gray-700 border-gray-300 hover:bg-gray-100 hover:text-indigo-600"
                                }`}
                                title={
                                  includeAuthInQuickCopy && log.curl_with_token && log.auth_token
                                    ? "Copy cURL (Includes Bearer / Auth Token)"
                                    : "Copy standard cURL"
                                }
                              >
                                <i className={`fas ${copiedCurlId === log._id ? "fa-check" : "fa-terminal"}`}></i>
                                <span>{copiedCurlId === log._id ? "Copied" : "cURL"}</span>
                              </button>

                              {/* Inspect Button */}
                              <button
                                type="button"
                                onClick={() => handleInspectLog(log)}
                                className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-semibold bg-indigo-50 text-indigo-700 border border-indigo-200 hover:bg-indigo-100 transition-colors"
                                title="Inspect request payload, headers, and responses"
                              >
                                <i className="fas fa-eye"></i>
                                <span>Inspect</span>
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination Footer */}
            {pagination.pages > 1 && (
              <div className="p-4 border-t border-gray-100 flex flex-col sm:flex-row items-center justify-between gap-3">
                <span className="text-xs text-gray-500">
                  Showing {(pagination.page - 1) * pagination.limit + 1} to{" "}
                  {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} records
                </span>
                <Pagination handlePageClick={handlePageChange} pageCount={pagination.pages} />
              </div>
            )}
          </div>
        </div>

        {/* DETAILED INSPECTION DRAWER / MODAL */}
        {selectedLog && (
          <div className="fixed inset-0 z-50 overflow-y-auto bg-black/50 backdrop-blur-xs flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl max-w-4xl w-full shadow-2xl border border-gray-200 overflow-hidden max-h-[90vh] flex flex-col animate-in fade-in zoom-in-95 duration-150">
              {/* Modal Header */}
              <div className="p-5 border-b border-gray-100 flex items-center justify-between bg-gray-50/80">
                <div className="flex items-center gap-2.5 flex-wrap">
                  <span
                    className={`px-2.5 py-1 rounded text-xs font-bold border ${
                      METHOD_STYLES[selectedLog.method] || "bg-gray-100 text-gray-800"
                    }`}
                  >
                    {selectedLog.method}
                  </span>
                  <span
                    className={`px-2.5 py-1 rounded text-xs font-bold border ${
                      selectedLog.status_code >= 500
                        ? "bg-rose-100 text-rose-800 border-rose-300"
                        : selectedLog.status_code >= 400
                        ? "bg-amber-100 text-amber-800 border-amber-300"
                        : "bg-emerald-100 text-emerald-800 border-emerald-300"
                    }`}
                  >
                    HTTP {selectedLog.status_code}
                  </span>
                  {/* Target Application Badge */}
                  {(() => {
                    const cfg = TARGET_APP_CONFIG[selectedLog.target_app] || TARGET_APP_CONFIG.other;
                    return (
                      <span className={`px-2.5 py-1 rounded text-xs font-semibold border ${cfg.badge}`}>
                        <i className={`${cfg.icon} mr-1`}></i>
                        {selectedLog.target_label || cfg.label}
                      </span>
                    );
                  })()}
                  <span className="font-mono text-xs text-gray-800 font-semibold truncate max-w-sm">
                    {selectedLog.path || selectedLog.url}
                  </span>
                </div>

                <button
                  type="button"
                  onClick={() => setSelectedLog(null)}
                  className="w-8 h-8 rounded-full bg-gray-200/80 text-gray-600 hover:bg-gray-300 flex items-center justify-center transition-colors"
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>

              {/* Modal Body */}
              <div className="p-6 overflow-y-auto space-y-6 flex-1 text-xs">
                {/* Error Alert Box (if present) */}
                {selectedLog.error_reason && (
                  <div
                    className={`p-4 rounded-xl border ${
                      selectedLog.status_code >= 500
                        ? "bg-rose-50 border-rose-200 text-rose-900"
                        : "bg-amber-50 border-amber-200 text-amber-900"
                    }`}
                  >
                    <div className="flex items-center gap-2 font-bold mb-1">
                      <i
                        className={`fas ${
                          selectedLog.status_code >= 500 ? "fa-exclamation-circle text-rose-600" : "fa-exclamation-triangle text-amber-600"
                        }`}
                      ></i>
                      <span>Error Diagnosis:</span>
                    </div>
                    <p className="font-mono text-xs mb-0">{selectedLog.error_reason}</p>

                    {selectedLog.error_stack && (
                      <details className="mt-3">
                        <summary className="cursor-pointer font-semibold text-[11px] text-rose-700 hover:underline">
                          View Stack Trace
                        </summary>
                        <pre className="mt-2 p-3 bg-gray-900 text-rose-200 rounded-lg overflow-x-auto text-[11px] font-mono leading-relaxed">
                          {selectedLog.error_stack}
                        </pre>
                      </details>
                    )}
                  </div>
                )}

                {/* Metadata Grid */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 bg-gray-50 p-4 rounded-xl border border-gray-100">
                  <div>
                    <span className="text-gray-400 block text-[10px] uppercase font-bold">Execution Time</span>
                    <span className="font-mono font-semibold text-gray-800">{selectedLog.duration_ms} ms</span>
                  </div>
                  <div>
                    <span className="text-gray-400 block text-[10px] uppercase font-bold">Client IP</span>
                    <span className="font-mono font-semibold text-gray-800">{selectedLog.ip || "unknown"}</span>
                  </div>
                  <div>
                    <span className="text-gray-400 block text-[10px] uppercase font-bold">User Context</span>
                    <span className="font-semibold text-gray-800 truncate block">
                      {selectedLog.user_email || (selectedLog.user_id ? `ID: ${selectedLog.user_id}` : "Anonymous / Guest")}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-400 block text-[10px] uppercase font-bold">Timestamp</span>
                    <span className="font-semibold text-gray-800">
                      {moment(selectedLog.createdAt).format("DD MMM, HH:mm:ss")}
                    </span>
                  </div>
                </div>

                {/* cURL Command Section with Auth Token Checkmark Option */}
                <div className="space-y-2.5">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                    <span className="font-bold text-gray-800 flex items-center gap-1.5 text-xs">
                      <i className="fas fa-terminal text-indigo-600"></i> Executable cURL Command
                    </span>

                    <div className="flex items-center gap-3">
                      {/* Token Toggle Option */}
                      <label className="inline-flex items-center gap-2 cursor-pointer bg-gray-100 hover:bg-gray-200 px-2.5 py-1 rounded-lg transition-colors border border-gray-200">
                        <input
                          type="checkbox"
                          checked={includeAuthInModalCurl}
                          onChange={(e) => setIncludeAuthInModalCurl(e.target.checked)}
                          className="rounded text-indigo-600 focus:ring-indigo-500 w-3.5 h-3.5"
                        />
                        <span className="text-xs font-semibold text-gray-800 flex items-center gap-1">
                          <i className="fas fa-key text-amber-500 text-[11px]"></i>
                          Include Bearer / Auth Token
                        </span>
                      </label>

                      {/* Copy cURL Button */}
                      <button
                        type="button"
                        onClick={() => handleCopyCurl(selectedLog, "modal", includeAuthInModalCurl)}
                        className="px-3 py-1 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 font-semibold text-xs transition-colors inline-flex items-center gap-1.5 shadow-2xs"
                      >
                        <i className={`fas ${copiedCurlId === "modal" ? "fa-check" : "fa-copy"}`}></i>
                        <span>{copiedCurlId === "modal" ? "Copied!" : "Copy cURL"}</span>
                      </button>
                    </div>
                  </div>

                  {/* Token Status Badge */}
                  <div className="flex items-center gap-2">
                    {selectedLog.auth_token ? (
                      <span className="inline-flex items-center gap-1 text-[11px] font-medium text-emerald-700 bg-emerald-50 px-2.5 py-0.5 rounded-full border border-emerald-200">
                        <i className="fas fa-check-circle text-[10px]"></i>
                        {includeAuthInModalCurl
                          ? "Auth token included in cURL (Ready to execute in Terminal)"
                          : "Auth token present on request (Currently masked in cURL view)"}
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-[11px] font-medium text-gray-500 bg-gray-100 px-2.5 py-0.5 rounded-full border border-gray-200">
                        <i className="fas fa-info-circle text-[10px]"></i>
                        No Authorization token was sent on this request
                      </span>
                    )}
                  </div>

                  {/* cURL Display Code Block */}
                  <pre className="p-3.5 bg-gray-900 text-emerald-400 rounded-xl overflow-x-auto font-mono text-[11px] leading-relaxed border border-gray-800 shadow-inner">
                    {includeAuthInModalCurl && selectedLog.curl_with_token
                      ? selectedLog.curl_with_token
                      : selectedLog.curl || "cURL could not be reconstructed."}
                  </pre>
                </div>

                {/* Request Body Viewer */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-gray-800 flex items-center gap-1.5">
                      <i className="fas fa-arrow-circle-right text-blue-600"></i> Request Body
                    </span>
                    {selectedLog.body && (
                      <button
                        type="button"
                        onClick={() => handleCopySnippet(selectedLog.body, "req_body")}
                        className="px-2 py-0.5 rounded text-[11px] bg-gray-100 text-gray-700 hover:bg-gray-200"
                      >
                        {copiedSection === "req_body" ? "Copied!" : "Copy Body"}
                      </button>
                    )}
                  </div>
                  {selectedLog.body && Object.keys(selectedLog.body).length > 0 ? (
                    <pre className="p-3 bg-gray-950 text-gray-100 rounded-xl overflow-x-auto font-mono text-[11px] max-h-48 border border-gray-800">
                      {JSON.stringify(selectedLog.body, null, 2)}
                    </pre>
                  ) : (
                    <div className="p-3 bg-gray-50 rounded-xl text-gray-400 italic border border-gray-100">
                      No request body sent
                    </div>
                  )}
                </div>

                {/* Response Body Viewer */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-gray-800 flex items-center gap-1.5">
                      <i className="fas fa-arrow-circle-left text-emerald-600"></i> Response Body
                    </span>
                    {selectedLog.response_body && (
                      <button
                        type="button"
                        onClick={() => handleCopySnippet(selectedLog.response_body, "resp_body")}
                        className="px-2 py-0.5 rounded text-[11px] bg-gray-100 text-gray-700 hover:bg-gray-200"
                      >
                        {copiedSection === "resp_body" ? "Copied!" : "Copy Response"}
                      </button>
                    )}
                  </div>
                  {selectedLog.response_body ? (
                    <pre className="p-3 bg-gray-950 text-gray-100 rounded-xl overflow-x-auto font-mono text-[11px] max-h-60 border border-gray-800">
                      {typeof selectedLog.response_body === "object"
                        ? JSON.stringify(selectedLog.response_body, null, 2)
                        : String(selectedLog.response_body)}
                    </pre>
                  ) : (
                    <div className="p-3 bg-gray-50 rounded-xl text-gray-400 italic border border-gray-100">
                      Empty response body
                    </div>
                  )}
                </div>

                {/* Request Headers Viewer */}
                <details className="space-y-2 pt-2 border-t border-gray-100">
                  <summary className="cursor-pointer font-bold text-gray-700 hover:text-indigo-600 flex items-center justify-between">
                    <span>
                      <i className="fas fa-list-ul mr-1.5 text-gray-400"></i> Sanitized Request Headers
                    </span>
                    <span className="text-[10px] text-gray-400 font-normal">Click to expand</span>
                  </summary>
                  <pre className="mt-2 p-3 bg-gray-50 text-gray-700 rounded-xl overflow-x-auto font-mono text-[11px] max-h-48 border border-gray-200">
                    {JSON.stringify(selectedLog.headers || {}, null, 2)}
                  </pre>
                </details>
              </div>

              {/* Modal Footer */}
              <div className="p-4 border-t border-gray-100 bg-gray-50 flex items-center justify-end">
                <button
                  type="button"
                  onClick={() => setSelectedLog(null)}
                  className="px-4 py-2 rounded-xl bg-gray-200 text-gray-800 hover:bg-gray-300 font-semibold text-xs transition-colors"
                >
                  Close Inspector
                </button>
              </div>
            </div>
          </div>
        )}

        {/* LOG RETENTION & DELETION MODAL */}
        {showDeleteModal && (
          <div className="fixed inset-0 z-50 overflow-y-auto bg-black/50 backdrop-blur-xs flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl max-w-lg w-full shadow-2xl border border-gray-200 overflow-hidden flex flex-col animate-in fade-in zoom-in-95 duration-150">
              {/* Header */}
              <div className="p-5 border-b border-gray-100 flex items-center justify-between bg-rose-50/70">
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-lg bg-rose-100 text-rose-600 flex items-center justify-center text-sm">
                    <i className="fas fa-trash-alt"></i>
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-gray-900">Manage Storage & Prune Logs</h3>
                    <p className="text-[11px] text-gray-500">Delete old or unnecessary API logs to optimize database space</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setShowDeleteModal(false)}
                  className="w-7 h-7 rounded-full bg-gray-200 text-gray-600 hover:bg-gray-300 flex items-center justify-center transition-colors"
                >
                  <i className="fas fa-times text-xs"></i>
                </button>
              </div>

              {/* Body */}
              <div className="p-6 space-y-4 text-xs">
                <span className="font-semibold text-gray-700 block">Select Deletion Criteria:</span>

                <div className="space-y-2">
                  {/* Preset 30 Days */}
                  <label
                    className={`flex items-center justify-between p-3 rounded-xl border cursor-pointer transition-all ${
                      deletionPreset === "older_than_30_days"
                        ? "bg-rose-50 border-rose-300 text-rose-900 font-semibold"
                        : "bg-gray-50 border-gray-200 hover:bg-gray-100 text-gray-700"
                    }`}
                  >
                    <div className="flex items-center gap-2.5">
                      <input
                        type="radio"
                        name="deletion_preset"
                        checked={deletionPreset === "older_than_30_days"}
                        onChange={() => setDeletionPreset("older_than_30_days")}
                        className="text-rose-600 focus:ring-rose-500"
                      />
                      <span>Logs older than 30 days</span>
                    </div>
                    <span className="text-[10px] text-gray-400">1 Month+</span>
                  </label>

                  {/* Preset 90 Days */}
                  <label
                    className={`flex items-center justify-between p-3 rounded-xl border cursor-pointer transition-all ${
                      deletionPreset === "older_than_90_days"
                        ? "bg-rose-50 border-rose-300 text-rose-900 font-semibold"
                        : "bg-gray-50 border-gray-200 hover:bg-gray-100 text-gray-700"
                    }`}
                  >
                    <div className="flex items-center gap-2.5">
                      <input
                        type="radio"
                        name="deletion_preset"
                        checked={deletionPreset === "older_than_90_days"}
                        onChange={() => setDeletionPreset("older_than_90_days")}
                        className="text-rose-600 focus:ring-rose-500"
                      />
                      <span>Logs older than 90 days</span>
                    </div>
                    <span className="text-[10px] text-gray-400">3 Months+</span>
                  </label>

                  {/* Preset 6 Months (180 Days) */}
                  <label
                    className={`flex items-center justify-between p-3 rounded-xl border cursor-pointer transition-all ${
                      deletionPreset === "older_than_180_days"
                        ? "bg-rose-50 border-rose-300 text-rose-900 font-semibold"
                        : "bg-gray-50 border-gray-200 hover:bg-gray-100 text-gray-700"
                    }`}
                  >
                    <div className="flex items-center gap-2.5">
                      <input
                        type="radio"
                        name="deletion_preset"
                        checked={deletionPreset === "older_than_180_days"}
                        onChange={() => setDeletionPreset("older_than_180_days")}
                        className="text-rose-600 focus:ring-rose-500"
                      />
                      <span>Logs older than 6 months</span>
                    </div>
                    <span className="text-[10px] text-gray-400">180 Days+</span>
                  </label>

                  {/* Preset 1 Year (365 Days) */}
                  <label
                    className={`flex items-center justify-between p-3 rounded-xl border cursor-pointer transition-all ${
                      deletionPreset === "older_than_365_days"
                        ? "bg-rose-50 border-rose-300 text-rose-900 font-semibold"
                        : "bg-gray-50 border-gray-200 hover:bg-gray-100 text-gray-700"
                    }`}
                  >
                    <div className="flex items-center gap-2.5">
                      <input
                        type="radio"
                        name="deletion_preset"
                        checked={deletionPreset === "older_than_365_days"}
                        onChange={() => setDeletionPreset("older_than_365_days")}
                        className="text-rose-600 focus:ring-rose-500"
                      />
                      <span>Logs older than 1 year</span>
                    </div>
                    <span className="text-[10px] text-gray-400">365 Days+</span>
                  </label>

                  {/* Custom Date Range */}
                  <label
                    className={`flex items-center justify-between p-3 rounded-xl border cursor-pointer transition-all ${
                      deletionPreset === "custom_range"
                        ? "bg-rose-50 border-rose-300 text-rose-900 font-semibold"
                        : "bg-gray-50 border-gray-200 hover:bg-gray-100 text-gray-700"
                    }`}
                  >
                    <div className="flex items-center gap-2.5">
                      <input
                        type="radio"
                        name="deletion_preset"
                        checked={deletionPreset === "custom_range"}
                        onChange={() => setDeletionPreset("custom_range")}
                        className="text-rose-600 focus:ring-rose-500"
                      />
                      <span>Custom Date Range</span>
                    </div>
                    <span className="text-[10px] text-gray-400">Specific range</span>
                  </label>

                  {/* Delete All Logs */}
                  <label
                    className={`flex items-center justify-between p-3 rounded-xl border cursor-pointer transition-all ${
                      deletionPreset === "all"
                        ? "bg-rose-100 border-rose-400 text-rose-900 font-bold"
                        : "bg-gray-50 border-gray-200 hover:bg-gray-100 text-gray-700"
                    }`}
                  >
                    <div className="flex items-center gap-2.5">
                      <input
                        type="radio"
                        name="deletion_preset"
                        checked={deletionPreset === "all"}
                        onChange={() => setDeletionPreset("all")}
                        className="text-rose-600 focus:ring-rose-500"
                      />
                      <span className="text-rose-700">Delete ALL logs (Full Purge)</span>
                    </div>
                    <span className="text-[10px] text-rose-500 uppercase font-bold">Irreversible</span>
                  </label>
                </div>

                {/* Custom Date Inputs if custom_range selected */}
                {deletionPreset === "custom_range" && (
                  <div className="p-3 bg-gray-50 rounded-xl border border-gray-200 space-y-2 mt-2">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-gray-600 font-medium">From:</span>
                      <input
                        type="datetime-local"
                        value={deletionStartDate}
                        onChange={(e) => setDeletionStartDate(e.target.value)}
                        className="text-xs rounded border border-gray-300 p-1.5 bg-white"
                      />
                    </div>
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-gray-600 font-medium">To:</span>
                      <input
                        type="datetime-local"
                        value={deletionEndDate}
                        onChange={(e) => setDeletionEndDate(e.target.value)}
                        className="text-xs rounded border border-gray-300 p-1.5 bg-white"
                      />
                    </div>
                  </div>
                )}

                {/* Impact Calculation Preview */}
                <div className="p-3.5 bg-amber-50 rounded-xl border border-amber-200 text-amber-900 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <i className="fas fa-exclamation-triangle text-amber-600"></i>
                    <span>Matching Logs:</span>
                  </div>
                  <div>
                    {isCountingDeletion ? (
                      <span className="text-xs text-gray-500 italic">Calculating...</span>
                    ) : (
                      <span className="text-sm font-bold text-amber-900">
                        {deletionCount !== null ? `${deletionCount.toLocaleString()} logs` : "N/A"}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Footer Buttons */}
              <div className="p-4 border-t border-gray-100 bg-gray-50 flex items-center justify-end gap-2.5">
                <button
                  type="button"
                  onClick={() => setShowDeleteModal(false)}
                  disabled={isDeleting}
                  className="px-4 py-2 rounded-xl bg-white text-gray-700 border border-gray-300 hover:bg-gray-100 font-semibold text-xs transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleDeleteLogs}
                  disabled={isDeleting || deletionCount === 0}
                  className="px-4 py-2 rounded-xl bg-rose-600 text-white hover:bg-rose-700 font-bold text-xs transition-colors shadow-xs flex items-center gap-2 disabled:opacity-50"
                >
                  {isDeleting && <i className="fas fa-spinner fa-spin"></i>}
                  <span>Confirm & Permanently Delete</span>
                </button>
              </div>
            </div>
          </div>
        )}
      </LoadingOverlay>
    </Layout>
  );
}
